import { useRef } from 'react';

export interface SegmentedOption<V extends string> {
  value: V;
  label: string;
  /** A Boxicon name, shown before the label. */
  icon?: string;
}

/**
 * Kridt's Segmented control (components.md, "Segmented control").
 *
 * A choice of PARAMETER — a time range, the theme — that changes how the same
 * content is shown. Switching to different content is tabs, underlined.
 *
 * One implementation of the radiogroup pattern, used by the time range and the
 * theme choice (spec 247), so the keyboard behaviour cannot drift between them:
 * one tab stop on the checked option, arrow keys move the choice and focus
 * with it. Below 720px the control takes its own full-width row (primitives.css).
 */
export function Segmented<V extends string>({
  options,
  value,
  onChange,
  label,
  testId,
  disabled,
}: {
  options: readonly SegmentedOption<V>[];
  value: V;
  onChange: (next: V) => void;
  /** The group's accessible name, e.g. "Date range". Required: a radiogroup without one is unnamed. */
  label: string;
  /** Group testid; each option gets `${testId}-${value}`. */
  testId?: string;
  /** While a change is being saved. */
  disabled?: boolean;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (dir: 1 | -1) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + dir + options.length) % options.length]!;
    onChange(next.value);
    // Focus follows the choice, as in a native radio group.
    requestAnimationFrame(() => refs.current[next.value]?.focus());
  };

  return (
    <div className="hub-segmented" role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map((opt) => {
        const checked = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => { refs.current[opt.value] = el; }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            data-option={opt.value}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
            disabled={disabled}
            className="hub-segmented__option"
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
            }}
          >
            {opt.icon && <i className={`bx ${opt.icon}`} aria-hidden="true" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
